import React, { useState } from "react";
import Table from "../../../core/common/dataTable/index";
import {
  getAttendanceDocument,
  getEmployeesAttendance,
} from "../../../core/data/redux/employeesAttendanceSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  ConvertUtcToLocal,
} from "../../../core/hooks/dateFormat";
import { Link } from "react-router-dom";
import AttendanceDetailsModel from "../../../core/modals/attendanceDetailsModel";
import MonthYearDropdown from "../attendance/MonthYearDropdown";
import { Avatar } from "antd";
import DeleteModal from "../../../core/modals/deleteModal";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import Api from "../../../core/data/redux/utils/apiCall";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateTime } from "../../../utils/dateUtils";
import AttendanceFilter, { AttendanceFilterValues } from "../../../core/common/AttendanceFilter";
import BulkAttendanceRegularizeModal from "./BulkAttendanceRegularizeModal";
import dayjs from "dayjs";

interface EmployeesAttendanceTableProps {
  userId?: number;
  setItem: (item: any) => void;
  type: string;
  onBulkRegularize?: (items: any[]) => void;
}

const EmployeesAttendanceTable = (props: EmployeesAttendanceTableProps) => {
  const { setItem, type } = props;
  const [month, setMonth] = useState<string>(
    (new Date().getMonth() + 1).toString()
  );
  const [year, setYear] = useState<string>(new Date().getFullYear().toString());
  const dispatch = useDispatch();
  const employeesAttendanceState = useSelector(
    (state: any) => state.employeesAttendanceState
  );
  const [dataList, setDataList] = React.useState<any[]>([]);
  const [originalDataList, setOriginalDataList] = React.useState<any[]>([]); // Store original unfiltered data
  const [deleteId, setDeleteId] = React.useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);
  const tableRef = React.useRef<any>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  // Filter state
  const [filterValues, setFilterValues] = useState<AttendanceFilterValues>({
    wing: "",
    department: "",
    office_location_id: "",
    date_from: "",
    date_to: "",
    empId: [],
    name: "",
  });

  console.log("employeesAttendanceState", employeesAttendanceState);

  const fetchRecords = (paginationData: any, customFilters?: AttendanceFilterValues) => {
    const activeFilters = customFilters || filterValues;
    const payload: any = {
      page: paginationData.current,
      limit: paginationData.pageSize,
      is_verified: type,
      month: month,
      year: year,
      date_from: activeFilters.date_from,
      date_to: activeFilters.date_to,
    };

    if (activeFilters.empId && activeFilters.empId.length > 0) {
      payload.empId = activeFilters.empId;
    }
    if (activeFilters.name) {
      payload.name = activeFilters.name;
    }

    dispatch(getEmployeesAttendance(payload));
  };

  // Frontend filtering logic as fallback for unsupported backend filters
  const applyFrontendFilters = (data: any[], filters: AttendanceFilterValues) => {
    let filteredData = [...data];

    // Filter by Wing
    if (filters.wing) {
      filteredData = filteredData.filter((item) => {
        const wingId = item?.employee?.details?.sap_department_designation_wings?.wing ||
                     item?.employee?.details?.sap_department_designation_wings?.wing_name;
        return wingId && wingId.toString() === filters.wing.toString();
      });
    }

    // Filter by Department
    if (filters.department) {
      filteredData = filteredData.filter((item) => {
        const deptId = item?.employee?.details?.sap_department_designation_wings?.department_id ||
                       item?.employee?.details?.sap_department_designation_wings?.department_name ||
                       item?.employee?.details?.department;
        return deptId && deptId.toString() === filters.department.toString();
      });
    }

    // Filter by Assigned Location (office_location_id)
    if (filters.office_location_id) {
      filteredData = filteredData.filter((item) => {
        const locId = item?.office_location_id || item?.officeLocation?.id;
        return locId && locId.toString() === filters.office_location_id.toString();
      });
    }

    // Filter by empId if selected
    if (filters.empId && filters.empId.length > 0) {
      filteredData = filteredData.filter((item) => {
        const itemEmpId = item?.employee?.empId || item?.emp_id;
        const normalizedItemEmpId = itemEmpId ? String(itemEmpId).padStart(8, "0") : "";
        return filters.empId!.some(
          (id) =>
            String(id) === String(itemEmpId) ||
            String(id).padStart(8, "0") === normalizedItemEmpId ||
            (parseInt(String(id), 10) === parseInt(String(itemEmpId), 10) && !isNaN(parseInt(String(id), 10)))
        );
      });
    }

    // Filter by name if entered
    if (filters.name) {
      const q = filters.name.toLowerCase().trim();
      filteredData = filteredData.filter((item) => {
        const details = item?.employee?.details || {};
        const first = details?.firstname || details?.first_name || "";
        const last = details?.lsastname || details?.lastname || details?.last_name || "";
        const fullName = `${first} ${last}`.toLowerCase();
        return fullName.includes(q);
      });
    }

    return filteredData;
  };

  // Handle filter change
  const handleFilterChange = (newFilters: AttendanceFilterValues) => {
    setFilterValues(newFilters);
  };

  // Handle search - Apply server-side filters for dates and frontend for others
  const handleSearch = () => {
    const newPagination = {
      ...pagination,
      current: 1,
    };
    setPagination(newPagination);
    fetchRecords(newPagination);
  };

  // Handle reset
  const handleReset = () => {
    const resetFilters: AttendanceFilterValues = {
      wing: "",
      department: "",
      office_location_id: "",
      date_from: "",
      date_to: "",
      empId: [],
      name: "",
    };
    setFilterValues(resetFilters);
    
    const newPagination = {
      ...pagination,
      current: 1,
    };
    setPagination(newPagination);

    // Fetch records with empty filters
    dispatch(
      getEmployeesAttendance({
        page: 1,
        limit: pagination.pageSize,
        is_verified: type,
        month: month,
        year: year,
      })
    );
  };

  // Download CSV function - Uses currently filtered data
  const handleDownloadCSV = () => {
    try {
      // Use currently displayed (filtered) data
      const data = dataList;
      
      if (data.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Generate CSV
      const csvHeaders = [
        "Sr. No.",
        "Emp ID",
        "Name",
        "Wing",
        "Department",
        "Assigned Location",
        "Check In Time",
        "Check Out Time",
        "Work Type",
        "Late Coming",
        "Early Leaving",
      ];

      const csvRows = data.map((item: any, index: number) => {
        const employee = item.employee;
        const details = employee?.details;
        const deptInfo = details?.sap_department_designation_wings;
        
        return [
          index + 1,
          employee?.empId ? parseInt(employee.empId, 10) : "",
          `${details?.firstname || ""} ${details?.lsastname || ""}`.trim(),
          deptInfo?.wing_name || "",
          deptInfo?.department_name || "",
          item.officeLocation?.address || "",
          item.check_in_time ? formatDateTime(item.check_in_time) : "",
          item.check_out_time ? ConvertUtcToLocal(item.check_out_time) : "",
          item.attendance_work_type || "",
          item.check_in === "L" ? "Yes" : "No",
          item.check_out === "E" ? "Yes" : "No",
        ];
      });

      // Create CSV content
      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((row: any[]) =>
          row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");

      // Download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `attendance_${type.toLowerCase()}_${month}_${year}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("CSV downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV");
    }
  };

  // React.useEffect(() => {
  //     fetchRecords(pagination);
  // }, []);

  React.useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    tableRef.current?.resetSelection([]);
    fetchRecords(pagination);
  }, [month, year, type]);

  React.useEffect(() => {
    if (
      employeesAttendanceState.loading === "fulfilled" &&
      employeesAttendanceState.action === "update"
    ) {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      tableRef.current?.resetSelection([]);
      setTimeout(function () {
        fetchRecords(pagination);
      }, 1000);
    } else if (
      employeesAttendanceState.loading === "fulfilled" &&
      employeesAttendanceState.action === "assign"
    ) {
      fetchRecords(pagination);
    } else if (
      employeesAttendanceState.loading === "fulfilled" &&
      employeesAttendanceState.action === "list"
    ) {
      const result = employeesAttendanceState?.data?.data || null;

      if (result && result.data && Array.isArray(result.data)) {
        const employees = result.data || [];
        setOriginalDataList(employees);
        
        // Apply frontend filters for fields not supported by the backend
        const filteredEmployees = applyFrontendFilters(employees, filterValues);
        
        const mappedData = filteredEmployees.map((item: any) => ({ ...item, key: item.id }));
        setDataList(mappedData);
        
        // Show all records selected by default when in PENDING mode
        if (type === "PENDING") {
          const allKeys = mappedData.map((item: any) => item.id ?? item.key);
          setSelectedRowKeys(allKeys);
          setSelectedRows(mappedData);
          tableRef.current?.resetSelection(allKeys);
        } else {
          setSelectedRowKeys([]);
          setSelectedRows([]);
          tableRef.current?.resetSelection([]);
        }

        setPagination({
          ...pagination,
          total: result.total || employees.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
        setOriginalDataList([]);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        tableRef.current?.resetSelection([]);
      }
    }
  }, [employeesAttendanceState.loading]);

  const [currentItem, setCurrentItem] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const showLocation = (record: any) => {
    console.log("---", record);
    setCurrentItem(record);
    setShowModal(true);
  };

  const randomAvatar = (firstName: string) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#87d068",
      "#d46b08",
    ];
    const index = Math.floor(Math.random() * colors.length);
    const initial = firstName?.charAt(0)?.toUpperCase() || "?";
    return {
      bg: colors[index],
      initial,
    };
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 100,
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
                pagination.current * pagination.pageSize -
                pagination.pageSize +
                1
              : index + 1}
          </div>
        </>
      ),
      //sorter: (a: any, b: any) => a.id.length - b.id.length,
    },
    {
      title: "EmpId",
      dataIndex: "employee",
      //sorter: (a: any, b: any) => a.employee.first_name.length - b.employee.first_name.length,
      render: (employee: any) =>
        employee?.empId ? parseInt(employee.empId, 10) : "",
    },
    {
      title: "Name",
      dataIndex: "employee",
      render: (text: string, record: any) => {
        const first = record?.employee?.details?.firstname?.trim() || "";
        const last =
          record?.employee?.details?.lsastname?.trim() ||
          record?.employee?.details?.lastname?.trim() ||
          "";

        if (!first && !last) {
          return <div style={{ height: "40px" }}></div>;
        }

        const avatarInfo = randomAvatar(first);

        return (
          <div className="d-flex align-items-center">
            {record?.employee?.sap_employee_images?.image ? (
              <Avatar
                src={`data:image/jpeg;base64,${record?.employee?.sap_employee_images?.image}`}
                className="me-2"
                style={{
                  backgroundColor: avatarInfo.bg,
                  verticalAlign: "middle",
                  width: "50px",
                  height: "50px",
                }}
              ></Avatar>
            ) : (
              <Avatar
                className="me-2"
                style={{
                  backgroundColor: avatarInfo.bg,
                  verticalAlign: "middle",
                  width: "50px",
                  height: "50px",
                }}
              >
                {avatarInfo.initial}
              </Avatar>
            )}
            <div>{`${first} ${last}`.trim()}</div>
          </div>
        );
      },
    },
    {
      title: "Wing",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.wing_name
          ? `${employee?.details?.sap_department_designation_wings?.wing_name}`
          : "",
    },
    {
      title: "Department",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.department_name
          ? `${employee?.details?.sap_department_designation_wings?.department_name}`
          : "",
    },
    {
      title: "Assigned Location",
      dataIndex: "officeLocation",
      render: (officeLocation: any) =>
        officeLocation?.address ? officeLocation.address : "-",
    },
    {
      title: "Designation",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.designation_name
          ? `${employee?.details?.sap_department_designation_wings?.designation_name}`
          : "",
    },
    {
      title: "Check In Time",
      dataIndex: "check_in_time",
      render: (text: string) => (text ? formatDateTime(text) : "N/A"),
    },
    {
      title: "Check Out Time",
      dataIndex: "check_out_time",
      sorter: (a: any, b: any) =>
        a.check_out_time.length - b.check_out_time.length,
      render: (check_out_time: any) =>
        check_out_time ? ConvertUtcToLocal(check_out_time) : "",
    },

    {
      title: "Check In Location",
      dataIndex: "check_in_latitude",
      render: (text: string, record: any) => (
        <div>
          <Link
            to={"#"}
            className="text-decoration-none"
            onClick={() => showLocation(record)}
          >
            <div>
              <b>Latitude</b>: {record.check_in_latitude}
            </div>
            <div>
              <b>Longitude</b>: {record.check_in_longitude}
            </div>
          </Link>
        </div>
      ),
    },
    {
      title: "Check out Location",
      dataIndex: "check_out_latitude",
      render: (text: string, record: any) => (
        <div>
          <Link
            to={"#"}
            className="text-decoration-none"
            onClick={() => showLocation(record)}
          >
            <div>
              <b>Latitude</b>: {record.check_out_latitude || "-"}
            </div>
            <div>
              <b>Longitude</b>: {record.check_out_longitude || "-"}
            </div>
          </Link>
        </div>
      ),
    },
    {
      title: "Work Type",
      dataIndex: "attendance_work_type",
      render: (text: any) => <span>{text}</span>,
    },

    {
      title: "Checkout Type",
      dataIndex: "checkout_remark",
      //sorter: (a: any, b: any) => a.checkout.length - b.checkout.length,
      render: (checkout_remark: any) => checkout_remark || "",
    },
    // {
    //     title: "Is Verified",
    //     dataIndex: "is_verified",
    //     sorter: (a: any, b: any) => a.is_verified.length - b.is_verified.length,
    //     render: (is_verified: any, record: any) => {
    //         return (
    //             <span style={{ minWidth: 100, padding: 2, textAlign: 'center', cursor: 'pointer' }} className={`badge ${(is_verified == 'VERIFIED') ? 'badge-success' : 'badge-danger'}  align-items-center badge-xs`} onClick={() => getDocumentUrl(record)}>
    //                 {is_verified}
    //             </span>
    //         )
    //     },
    // },
    {
      title: "Late coming",
      dataIndex: "check_in",
      render: (value: string) => {
        if (value === "L") {
          return <span style={{ color: "red" }}>Yes</span>;
        } else if (value === "E") {
          return <span style={{ color: "green" }}>No</span>;
        } else {
          return <span style={{ color: "green" }}>No</span>;
        }
      },
    },

    {
      title: "Early leaving",
      dataIndex: "check_out",
      render: (value: string) => {
        if (value === "E") {
          return <span style={{ color: "green" }}>Yes</span>;
        } else if (value === "L") {
          return <span style={{ color: "red" }}>No</span>;
        } else {
          return <span style={{ color: "red" }}>No</span>;
        }
      },
    },

    // Day Type column - only shown for REJECTED tab
    ...(type === "REJECTED"
      ? [
          {
            title: "Day Type",
            dataIndex: "day_type",
            render: (text: string) => text,
          },
        ]
      : []),

    {
      title: "Actions",
      dataIndex: "actions",
      width: 120,
      render: (text: string, record: any, index: number) => (
        <div className="action-icon d-inline-flex" style={{ gap: "10px" }}>
          {record.is_verified === "PENDING" && (
            <Link
              to="#"
              title="Regularise"
              onClick={() => getDocumentUrl(record)}
              style={{ color: "#28a745" }}
            >
              <i className="ti ti-checkbox" style={{ fontSize: "20px" }} />
            </Link>
          )}
          <Link
            to="#"
            title="Delete"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() => setDeleteId(record.id)}
            style={{ color: "#dc3545" }}
          >
            <i className="ti ti-trash" style={{ fontSize: "20px" }} />
          </Link>
        </div>
      ),
    },
    // {
    //   title: "Actions",
    //   dataIndex: "actions",
    //   width:100,
    //   render: (text: string, record: any, index: number) => (
    //     <div className="action-icon d-inline-flex">
    //       <button className="btn btn-primary btn-sm" data-bs-toggle="modal" data-inert={true} data-bs-target="#assign_office_modal" onClick={() => setCurrentItem(record)}>
    //         <i className="ti ti-square-rounded-plus me-2" /> V
    //       </button>
    //     </div>
    //   ),
    // }
  ];

  const [isLoading, setIsLoading] = React.useState(false);
  const handleDelete = async (response: string) => {
    if (response === "YES" && deleteId) {
      console.log("Delete item:", deleteId);
      try {
        setIsLoading(true);
        const response = await Api.post(`${API_URLS.DELETE_ATTENDANCE}`, {
          id: deleteId,
        });
        setIsLoading(false);
        toast.success(
          response?.data?.message || "Attendance deleted successfully"
        );
        fetchRecords(pagination);
        setDeleteId(null);
      } catch (error: any) {
        setIsLoading(false);
        const errorMessage =
          error?.response?.data?.data?.message || "Failed to fetch data.";
        toast.error(errorMessage);
      }
    }
    setDeleteId(null);
  };

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    fetchRecords(pagination);
  };

  const getDocumentUrl = async (record: any) => {
    setItem(record);
    await dispatch(getAttendanceDocument({ id: record.id }));
  };

  return (
    <>
      <style>{`
        .ant-table-wrapper .ant-table-tbody .ant-table-row.ant-table-row-selected > .ant-table-cell {
          background-color: #F0F7FF !important;
        }
        .ant-table-wrapper .ant-table-tbody .ant-table-row.ant-table-row-selected {
          outline: 1.5px solid #3B82F6 !important;
          outline-offset: -1px;
        }
      `}</style>
      <div>
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <div className="d-flex align-items-center gap-3">
              <h4 className="mb-4 mt-3">Attendance Regularize</h4>
              {type === "PENDING" && (
                <button
                  type="button"
                  className="btn btn-sm mb-4 mt-3 d-flex align-items-center gap-2"
                  disabled={dataList.length === 0}
                  onClick={() => {
                    setShowBulkModal(true);
                  }}
                  style={{
                    borderRadius: "6px",
                    fontWeight: 500,
                    backgroundColor: "#C0282C",
                    borderColor: "#C0282C",
                    color: "#fff",
                    cursor: dataList.length > 0 ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    padding: "6px 14px",
                  }}
                >
                  <i className="ti ti-checklist fs-16" />
                  Bulk Regularize {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : `All (${dataList.length})`}
                </button>
              )}
            </div>
            <MonthYearDropdown
              onChange={({ month, year }) => {
                setMonth(month);
                setYear(year);
              }}
              month={""}
              year={year}
            />
          </div>

          {/* Bulk Selection Banner */}
          {type === "PENDING" && selectedRowKeys.length > 0 && (
            <div
              className="d-flex align-items-center justify-content-between mx-3 mt-3 px-3 py-2"
              style={{
                backgroundColor: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: "8px",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-checklist" style={{ color: "#C0282C", fontSize: "18px" }} />
                <span style={{ fontSize: "13px", color: "#991B1B", fontWeight: 500 }}>
                  <strong>{selectedRowKeys.length}</strong> attendance record
                  {selectedRowKeys.length > 1 ? "s" : ""} selected for bulk regularization
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-1"
                  onClick={() => setShowBulkModal(true)}
                  style={{
                    backgroundColor: "#C0282C",
                    borderColor: "#C0282C",
                    color: "#fff",
                    fontWeight: 500,
                    fontSize: "12px",
                    padding: "4px 12px",
                    borderRadius: "6px",
                  }}
                >
                  <i className="ti ti-check" />
                  Regularize Selected ({selectedRowKeys.length})
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "6px" }}
                  onClick={() => {
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    tableRef.current?.resetSelection([]);
                  }}
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {/* Filter Section */}
          <div className="px-3 pt-3">
            <AttendanceFilter
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onReset={handleReset}
              onDownloadCSV={handleDownloadCSV}
              showDownload={true}
              attendanceData={originalDataList}
            />
          </div>
          <div className="card-body p-0 py-3">
            <Table
              ref={tableRef}
              columns={columns}
              dataSource={dataList}
              Selection={type === "PENDING"}
              showSearch={false}
              selectedRowKeys={selectedRowKeys}
              onRowSelection={(keys: any[]) => {
                setSelectedRowKeys(keys);
                const selected = dataList.filter((item: any) =>
                  keys.includes(item.id ?? item.key)
                );
                setSelectedRows(selected);
              }}
              getCheckboxProps={(record: any) => ({
                disabled: record.is_verified !== "PENDING",
              })}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={employeesAttendanceState.loading === "pending"}
            />
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-md-12">
            <div className="datatable-paginate mt-4" />
          </div>
        </div>
      </div>
      {currentItem && (
        <AttendanceDetailsModel
          show={showModal}
          item={currentItem}
          close={() => {
            setShowModal(false);
            setCurrentItem(null);
          }}
        />
      )}
      <DeleteModal handleDelete={handleDelete} />
      <BulkAttendanceRegularizeModal
        open={showBulkModal}
        items={selectedRows.length > 0 ? selectedRows : dataList}
        onClose={() => setShowBulkModal(false)}
        onSuccess={() => {
          setSelectedRowKeys([]);
          setSelectedRows([]);
          tableRef.current?.resetSelection([]);
          fetchRecords(pagination);
        }}
      />
    </>
  );
};

export default EmployeesAttendanceTable;
